import type BigNumber from 'bignumber.js';
import { useGetBittensorNativeBalances, useGetPool } from 'clients/api';

import { NULL_ADDRESS } from 'constants/address';
import type { Token, TokenAction, TokenBalance } from 'types';
import { areTokensEqual, convertMantissaToTokens, convertTokensToMantissa } from 'utilities';
import type { Address } from 'viem';
import type { BittensorNativeSupplySource } from '../SupplyForm/getBittensorSupplySourceTokens';
import { getBittensorSupplySourceTokens } from '../SupplyForm/getBittensorSupplySourceTokens';
import { getMarketCapabilities } from '../shared/getMarketCapabilities';
import type { VTokenWrapperKind } from '../shared/getVTokenWrapperKind';

export interface UseGetOperationFormTokenBalancesInput {
  poolComptrollerContractAddress: Address;
  underlyingToken: Token;
  wrapperKind: VTokenWrapperKind;
  isIntegratedSwapFeatureEnabled: boolean;
  canWrapNativeToken: boolean;
  action: TokenAction;
  accountAddress?: Address;
}

export interface UseGetOperationFormTokenBalancesOutput {
  tokenBalances: TokenBalance[];
  userWalletNativeTokenBalanceTokens?: BigNumber;
  bittensorNativeSupplySources: BittensorNativeSupplySource[];
}

export const useGetOperationFormTokenBalances = ({
  poolComptrollerContractAddress,
  accountAddress,
  underlyingToken,
  wrapperKind,
  isIntegratedSwapFeatureEnabled,
  action,
  canWrapNativeToken,
}: UseGetOperationFormTokenBalancesInput): UseGetOperationFormTokenBalancesOutput => {
  const { data: getPoolData } = useGetPool({
    poolComptrollerAddress: poolComptrollerContractAddress,
    accountAddress,
  });

  const { data: bittensorNativeBalances } = useGetBittensorNativeBalances(
    { accountAddress: accountAddress ?? NULL_ADDRESS },
    { enabled: !!accountAddress },
  );

  const assets = getPoolData?.pool.assets || [];

  const wrappedUnderlyingToken = underlyingToken.tokenWrapped;

  let userWalletNativeTokenBalanceTokens: BigNumber | undefined;
  const tokenBalances: TokenBalance[] = [];

  for (const asset of assets) {
    const isWrappedToken = !!asset.vToken.underlyingToken.tokenWrapped;
    const isWrappedUnderlyingToken =
      wrappedUnderlyingToken &&
      areTokensEqual(asset.vToken.underlyingToken, wrappedUnderlyingToken);

    const shouldIncludeTokenBalance =
      isIntegratedSwapFeatureEnabled ||
      (canWrapNativeToken && (isWrappedToken || isWrappedUnderlyingToken)) ||
      areTokensEqual(underlyingToken, asset.vToken.underlyingToken);

    const isPaused = asset.disabledTokenActions.includes(action);

    if (shouldIncludeTokenBalance && !isPaused) {
      tokenBalances.push({
        token: asset.vToken.underlyingToken,
        balanceMantissa: convertTokensToMantissa({
          token: asset.vToken.underlyingToken,
          value: asset.userWalletBalanceTokens,
        }),
      });
    }

    if (asset.vToken.underlyingToken.isNative) {
      userWalletNativeTokenBalanceTokens = asset.userWalletBalanceTokens;
    }
  }

  const bittensorSupplySourceTokens = getBittensorSupplySourceTokens({
    underlyingToken,
    wrapperKind,
    bittensorNativeBalances,
  });
  const capabilities = getMarketCapabilities({
    underlyingToken,
    wrapperKind,
    bittensorNativeBalances,
  });

  if (capabilities.canUseNativeTao && underlyingToken.tokenWrapped && bittensorNativeBalances) {
    userWalletNativeTokenBalanceTokens = convertMantissaToTokens({
      token: underlyingToken.tokenWrapped,
      value: bittensorNativeBalances.nativeTaoBalanceMantissa,
    });
  }

  return {
    tokenBalances: [...tokenBalances, ...bittensorSupplySourceTokens.tokenBalances],
    userWalletNativeTokenBalanceTokens,
    bittensorNativeSupplySources: bittensorSupplySourceTokens.nativeSupplySources,
  };
};
